import { brandColors, memberLimitedLockColor } from '@assets/colors/brandColors';
import BookmarkIcon from '@assets/images/common/icon/bookmark/BookmarkIcon';
import HeartIcon from '@assets/images/common/icon/heart/HeartIcon';
import LockIcon from '@assets/images/common/icon/lock/LockIcon';
import PhotoGrayIcon from '@assets/images/common/image_dummy.svg';
import { Article } from '@src/api/types/article.ts';
import BookmarkLoginPopup from '@src/components/BookmarkLoginPopup';
import FavoriteLoginPopup from '@src/components/FavoriteLoginPopup';
import { SnsShareButtons } from '@src/components/SnsShareButtons';
import ArticleLoginPrompt from '@src/features/shared/ArticleLoginPrompt';
import { useProcessedTableContent } from '@src/hooks/useProcessedContent';
import Tag from '@src/ui/elements/Company/Tag';
import { formatLikesCount } from '@src/utils/stringUtils';
import dayjs from 'dayjs';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import ArticleItem from '../company/articles/components/ArticleItem';

type Props = {
  article: Article;
  relatedArticles: Article[];
  isLoggedIn: boolean;
  registrationUrl: string;
  loginUrl: string;
  shareData: {
    url: string;
    title: string;
  } | null;
  isBookmarked?: boolean;
  onBookmarkToggle?: () => void;
  onRelatedArticleBookmarkToggle?: (articleId: number) => void;
  onRelatedArticleFavoriteToggle?: (articleId: number) => void;
  onBookmarkClickWhenLoggedOut?: () => void;
  onRelatedArticleBookmarkClickWhenLoggedOut?: (id: string) => void;
  visiblePopupId?: string | null;
  popupRef?: React.RefObject<HTMLDivElement>;
  bookmarkPopupId?: string;
  onPopupClose?: () => void;
  isLiked?: boolean;
  likesCount?: number;
  onLikeToggle?: () => void;
  likePopupId?: string;
};

const ArticleContent = ({
  article,
  relatedArticles,
  isLoggedIn,
  registrationUrl,
  loginUrl,
  shareData,
  isBookmarked = false,
  onBookmarkToggle,
  onRelatedArticleBookmarkToggle,
  onRelatedArticleFavoriteToggle,
  onBookmarkClickWhenLoggedOut,
  onRelatedArticleBookmarkClickWhenLoggedOut,
  visiblePopupId,
  popupRef,
  bookmarkPopupId,
  onPopupClose,
  isLiked = false,
  likesCount = 0,
  onLikeToggle,
  likePopupId,
}: Props) => {
  const processedContent = useProcessedTableContent(article?.content || '');
  const isMemberLimited = article?.is_member_limited ?? false;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article?.title,
    datePublished: article?.published_at,
    dateModified: article?.updated_at,
    ...(article?.description && { description: article.description }),
    ...(article?.thumbnail_url && { image: article.thumbnail_url }),
    ...(article?.category?.name && {
      articleSection: article.category.name,
    }),
    ...(article?.tags &&
      article.tags.length > 0 && {
        keywords: article.tags.map((tag) => tag.name).join(', '),
      }),
  };

  return (
    <>
      <Helmet>
        <script type='application/ld+json'>{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <div className='lg:mt-10'>
        <style>{`
          [data-article-intro-grid] {
            grid-template-areas: 'title title' 'meta pickup' 'tag tag';
          }
          @media (max-width: 1024px) {
            [data-article-intro-grid] {
              grid-template-areas: 'meta' 'title' 'tag' 'pickup';
            }
          }
          @media (max-width: 768px) {
            .tiptap-content table {
              width: 100% !important;
              font-size: 14px;
            }
            .tiptap-content table th,
            .tiptap-content table td {
              padding: 8px 4px !important;
              word-break: break-word;
            }
          }
        `}</style>
        <div
          className='grid grid-cols-[1fr_auto] gap-6 lg:grid-cols-[100%] md:relative'
          data-article-intro-grid
        >
          <h1
            className='break-all text-3xl font-bold leading-normal md:text-xl'
            style={{ gridArea: 'title' }}
          >
            {article?.title}
          </h1>
          <div
            className='flex items-center gap-4 lg:justify-between'
            style={{
              gridArea: 'meta',
            }}
          >
            <Link to={`/articles?category_slug=${article.category?.slug}`}>
              <p className='line-clamp-1 border-b-2 border-solid border-b-primary py-1 text-[15px] text-primary'>
                {article.category?.name}
              </p>
            </Link>
            <div className='contents lg:flex lg:items-center lg:gap-4'>
              <time
                className='whitespace-nowrap text-[14px]'
                dateTime={article.published_at}
              >
                公開日：{dayjs(article.published_at).format('YYYY-MM-DD')}
              </time>
              {isMemberLimited && (
                <LockIcon
                  fillColor={memberLimitedLockColor}
                  width={21}
                  height={21}
                  role='img'
                  aria-label='会員限定'
                  className='shrink-0'
                />
              )}
              <div className='flex items-center gap-2 md:static'>
                <div className='relative md:static'>
                  <div className='flex items-center gap-1'>
                    <button
                      type='button'
                      onClick={onLikeToggle}
                      aria-label={isLiked ? 'いいね解除' : 'いいね'}
                      data-testid='like-button'
                      className={`mt-[2px] transition-opacity ${onLikeToggle ? 'hover:opacity-60' : ''}`}
                    >
                      <HeartIcon
                        fillColor={isLiked ? '#DD2B5C' : '#5F6368'}
                        filled={isLiked}
                        width={16}
                        height={16}
                      />
                    </button>
                    <span
                      className={`-mt-px text-sm ${isLiked ? 'text-like' : 'text-gray-400'}`}
                      data-testid='like-count'
                    >
                      {formatLikesCount(likesCount)}
                    </span>
                  </div>
                  {popupRef && likePopupId && (
                    <FavoriteLoginPopup
                      isVisible={visiblePopupId === likePopupId}
                      popupRef={popupRef}
                      onClose={onPopupClose}
                    />
                  )}
                </div>
                <div className='relative md:static'>
                  <button
                    type='button'
                    onClick={() => {
                      if (onBookmarkToggle) {
                        onBookmarkToggle();
                      }
                      if (onBookmarkClickWhenLoggedOut) {
                        onBookmarkClickWhenLoggedOut();
                      }
                    }}
                    className={`mt-1.5 transition-opacity ${onBookmarkToggle || onBookmarkClickWhenLoggedOut ? 'hover:opacity-60' : ''}`}
                    aria-label={isBookmarked ? 'ブックマーク解除' : 'ブックマーク'}
                    data-testid='bookmark-button'
                  >
                    <BookmarkIcon
                      fillColor={isBookmarked ? brandColors[500] : '#6B7280'}
                      filled={isBookmarked}
                      width={16}
                      height={16}
                    />
                  </button>
                  {popupRef && bookmarkPopupId && (
                    <BookmarkLoginPopup
                      isVisible={visiblePopupId === bookmarkPopupId}
                      popupRef={popupRef}
                      onClose={onPopupClose}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            style={{ gridArea: 'pickup' }}
            className='pickup-container'
          >
            {article.is_pickup && <span className='text-primary'>ピックアップ記事</span>}

            <div className='pickup-share-buttons'>
              {shareData && (
                <SnsShareButtons
                  url={shareData.url}
                  title={shareData.title}
                  variant='compact'
                />
              )}
            </div>
          </div>
          <ul
            className='flex flex-wrap gap-2'
            style={{
              gridArea: 'tag',
            }}
          >
            {article.tags?.map((tag, index) => (
              <li key={index}>
                <Tag
                  to={`/articles?tag_slug=${tag.slug}`}
                  label={tag.name}
                />
              </li>
            ))}
          </ul>
        </div>
        <div
          className='tiptap-content mt-12 overflow-hidden'
          dangerouslySetInnerHTML={{
            __html: processedContent,
          }}
        />
        {isMemberLimited && !isLoggedIn && (
          <div className='mt-8'>
            <ArticleLoginPrompt
              registrationUrl={registrationUrl}
              loginUrl={loginUrl}
            />
          </div>
        )}
        {article?.original_pdf_name && (!isMemberLimited || isLoggedIn) && (
          <div className='mt-12'>
            <a
              href={article?.pdf_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-base text-primary hover:underline'
            >
              {article.original_pdf_name}
            </a>
          </div>
        )}
        {shareData && (
          <div className='mt-10 px-6'>
            <SnsShareButtons
              url={shareData.url}
              title={shareData.title}
              variant='full'
            />
          </div>
        )}
      </div>
      <div className='mt-6 border-t border-solid border-t-gray-200 py-12 lg:mt-2'>
        <h2 className='text-lg font-semibold md:text-sm'>関連記事</h2>
        <div className='mt-10 grid grid-cols-3 gap-x-8 gap-y-10 md:mt-8 md:grid-cols-1'>
          {relatedArticles.map((article, index) => (
            <ArticleItem
              key={index}
              to={`/articles/${article.id}`}
              title={article.title}
              content={article.content}
              hideMobileDescription
              date={dayjs(article.published_at).format('YYYY-MM-DD')}
              category={{
                id: article.category?.id ?? 0,
                name: article.category?.name ?? '',
                slug: article.category?.slug ?? '',
              }}
              tags={
                article.tags?.map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  slug: tag.slug,
                })) ?? []
              }
              thumbnail={article.thumbnail_url || PhotoGrayIcon}
              isMemberLimited={article.is_member_limited}
              lockIconMdClassName='md:mt-[3px] md:size-[15px]'
              isBookmarked={article.is_bookmarked ?? false}
              isLiked={article.favorite?.is_favorite}
              likesCount={article.favorite?.favorites_count}
              onFavoriteToggle={
                onRelatedArticleFavoriteToggle
                  ? () => onRelatedArticleFavoriteToggle(article.id)
                  : undefined
              }
              favoritePopupId={
                onRelatedArticleFavoriteToggle ? `related-favorite-${article.id}` : undefined
              }
              onBookmarkToggle={
                onRelatedArticleBookmarkToggle
                  ? () => onRelatedArticleBookmarkToggle(article.id)
                  : undefined
              }
              onBookmarkClickWhenLoggedOut={
                onRelatedArticleBookmarkClickWhenLoggedOut
                  ? () => onRelatedArticleBookmarkClickWhenLoggedOut(`related-${article.id}`)
                  : undefined
              }
              bookmarkPopupId={`related-${article.id}`}
              visiblePopupId={visiblePopupId}
              popupRef={popupRef}
              onPopupClose={onPopupClose}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default ArticleContent;
